/** @jsxImportSource @emotion/react */
import getConfig from 'next/config'
import { useTranslation } from 'react-i18next'
import { Box, Button, Spinner, Text } from 'grommet'

import { routes } from 'routes'
import { useAuth } from 'hooks/useAuth'
import { Checkmark } from 'grommet-icons'
import { BorderType } from 'grommet/utils'
import useDeviceSize from 'hooks/useDevice'
import LinkButton from 'components/LinkButton'
import { dropHoverStyle } from 'util/ui-tables'

export const planColors = ['#76d89a', '#d34a9d', '#320064']

const {
  publicRuntimeConfig: { PADDLE_PERSONAL_PREMIUM },
} = getConfig()

type PlanFeature = {
  slug: string
  id: string
  price: number
  subtitle: string
  items: Feature[]
}
type Feature = {
  title: string
  included?: boolean
}
export const planFeatures: PlanFeature[] = [
  {
    slug: 'personal',
    id: PADDLE_PERSONAL_PREMIUM,
    price: 9,
    subtitle: 'Unlock your group on-demand mental health coach',
    items: [
      { title: 'Unlimited participants' },
      { title: 'Unlimited creation of groups' },
      { title: 'Mood guides tailored to your emotional climate' },
    ],
  },
]

type Props = {
  productIdLoading: string | false
  handleCheckout?: (product: string) => void | Promise<void>
}

export default function PlanGrid({ productIdLoading, handleCheckout }: Props) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { small, medium, direction } = useDeviceSize()
  return (
    <Box justify="center" direction={direction} gap={medium ? 'small' : '1.5rem'}>
      {planFeatures.map((product, key) => {
        const amount = product.price
        const isFree = amount === 0
        const border: BorderType =
          key === 1
            ? [
                { size: '1px', side: 'all', color: planColors[key] },
                { size: '.35rem', side: 'top', color: planColors[key] },
              ]
            : { size: '.35rem', side: 'top', color: planColors[key] }
        return (
          <Box
            width={small ? '100%' : undefined}
            key={product.id}
            background="white"
            css={dropHoverStyle}
            round={{ size: 'small' }}
            border={border}>
            <Box direction="column" align="center">
              <Box margin={{ vertical: 'small' }} />
              <PlanPrice amount={amount} />
              <Box margin={{ vertical: 'small' }} pad={{ horizontal: 'large' }} fill="horizontal">
                {isFree ? (
                  user ? (
                    <Button
                      disabled={!user?.app_metadata?.plan}
                      color={planColors[key]}
                      label={
                        <Text color={planColors[key]}>
                          {!user?.app_metadata?.plan ? t('basic.current-plan') : t('cta.upgrade')}
                        </Text>
                      }
                    />
                  ) : (
                    <LinkButton
                      primary
                      href={routes.signup}
                      color={planColors[key]}
                      label={<Text color="darkgreen">{t('cta.get-started')}</Text>}
                    />
                  )
                ) : user ? (
                  <Button
                    primary
                    color={planColors[key]}
                    disabled={productIdLoading === product.id}
                    onClick={() => handleCheckout && handleCheckout(product.id)}
                    icon={productIdLoading === product.id ? <Spinner /> : undefined}
                    label={
                      <Text color="white" textAlign="center">
                        {planFeatures[key].id === user?.app_metadata?.plan?.subscription_plan_id
                          ? 'Manage'
                          : t('cta.subscribe-now')}
                      </Text>
                    }
                  />
                ) : (
                  <LinkButton
                    primary
                    href={routes.signup}
                    color={planColors[key]}
                    label={<Text color="white">{t('cta.get-started')}</Text>}
                  />
                )}
              </Box>

              <Box
                gap="small"
                fill="horizontal"
                direction="column"
                margin={{ top: 'small', bottom: 'medium' }}
                pad={{ horizontal: 'large', bottom: '1rem' }}>
                <Text size="small" weight={600} textAlign={medium ? 'center' : 'start'}>
                  {planFeatures[key].subtitle}
                </Text>
                {planFeatures[key].items.map((item, index) => (
                  <Box key={index} direction={medium ? 'column' : 'row'} gap="small" align="center">
                    <Checkmark color="status-ok" />
                    <Text size="small" textAlign={medium ? 'center' : 'start'}>
                      {item.title}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export const PlanPrice = ({ amount }: { amount: number }) => {
  const { t } = useTranslation()
  const priceString = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount)
  return (
    <Box direction="row" justify="center" align="end" gap="xsmall">
      <Text size="2rem" textAlign="center" weight="bold">
        {priceString}
      </Text>
      <Text size="1.5rem" textAlign="center">
        /
      </Text>
      <Text size="medium" textAlign="center">
        {t('month')}
      </Text>
    </Box>
  )
}
